import { CircularProgress } from '@mui/material';
import { useOnboardingStore } from 'store/useOnboardingStore';
import { FixedFeatures, MAX_LIMIT, PlanPeriods, SubscriptionPlans, TickPath } from 'util/Constants';
import { PlanTypes } from 'util/Types';
import IntroductionSection from './Introduction';
import { HelloMuiLoadingButton } from 'components';

const DISCOUNT_PERCENT = 0.2;

const PeriodSelector = () => {
    const { duration, setDuration } = useOnboardingStore();

    return (
        <div className="mx-auto w-fit ">
            <div className="rounded-[60px] h-[48px] border border-my-gray border-opacity-60 p-[6px] grid grid-cols-2 bg-white relative">
                <div
                    className={`absolute top-[5px] h-[36px] w-[calc(50%-6px)] bg-my-blue rounded-[60px] transition-all duration-300 ease-in-out transform ${duration === 1 ? 'left-[6px]' : 'left-[calc(50%+3px)]'}`}
                />
                {PlanPeriods.map((period, index) => (
                    <div
                        className={`cursor-pointer rounded-[60px] h-full relative z-10 transition-colors duration-300 ${duration === period.duration ? 'text-white' : 'text-gray-700'}`}
                        key={index}
                        onClick={() => setDuration(period.duration)}
                    >
                        <div className="w-full h-full flex items-center justify-center px-[32px]">
                            <span className="text-[14px] leading-none">{period.description}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex justify-end pt-[5px]">
                <img alt={'Pricing Up '} src="/image/OnBoarding/Onboarding-Pricing-ArrowUp.png" />
                <span className="text-my-blue font-semibold">{`Save ${DISCOUNT_PERCENT * 100}%`}</span>
            </div>
        </div>
    );
};

const PlanCard = ({ plan }: { plan: PlanTypes }) => {
    const { duration, setPlan, payPricing, isPaymentLoading } = useOnboardingStore();
    const monthlyPrice = duration === 1 ? plan.pricePerMonth : plan.pricePerYearMonthly;
    const features = [
        plan.title === 'Entry' ? 'All rentals up to €3,000/ month' : 'All rentals',
        plan.title === 'Entry'
            ? 'All sales properties up to €400,000'
            : plan.title === 'Plus'
              ? 'All sales properties up to €1,000,000'
              : plan.title === 'Premium'
                ? 'All sales properties up to €2,000,000'
                : 'All sales properties from €2,000,000',
        'AI Search',
        "❤️'s & shareable link",
        ...(plan.title === 'Pro' ? ['All Price On Request Properties'] : []),
    ];

    const handlePlanSelect = async () => {
        if (isPaymentLoading) return;

        try {
            setPlan(plan.title);
            await payPricing();
        } catch (error) {
            console.error('Payment error:', error);
        }
    };

    return (
        <div
            className={`cursor-pointer hover:shadow-hello rounded-[20px] bg-white bg-opacity-40 border border-my-gray border-opacity-60 px-[20px] py-[24px] mx-[30px] md:mx-0 ${isPaymentLoading ? 'pointer-events-none opacity-50' : ''}`}
            onClick={handlePlanSelect}
        >
            <img alt={`Plan Icon (${plan.title})`} src={plan.icon} />
            <div className="font-semibold text-[20px]">{plan.title}</div>
            <div>
                <div className="flex items-baseline">
                    <span className="text-[36px] font-semibold transition-all duration-300 ease-in-out transform">{`€${monthlyPrice}`}</span>
                    <span className="text-[12px] ml-1 transition-opacity duration-300">{`/month`}</span>
                </div>
                <div
                    className={`text-[11px] text-gray-500 transition-all duration-300 ease-in-out transform ${duration === 12 ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 h-0'}`}
                >
                    {`€${plan.pricePerYear} billed yearly`}
                </div>
            </div>

            <div className="border border-b-my-gray border-opacity-40"></div>
            <div className="text-[12px] flex flex-col gap-[16px] py-[16px]">
                <div className="font-semibold text-[14px]">{'Features'}</div>
                {features.map((feature, index) => (
                    <div className="flex gap-[5px] items-start" key={index}>
                        <img alt={`Tick ${index}`} src={TickPath} className="mt-1" />
                        <div className="break-words">{feature}</div>
                    </div>
                ))}
            </div>
            <HelloMuiLoadingButton
                variant="outlined"
                className="!rounded-[5px] h-[42px] w-full !border-my-black !border-opacity-10"
                loadingIndicator={<CircularProgress color="primary" size={24} />}
                loading={isPaymentLoading}
                disabled={isPaymentLoading}
                sx={{
                    color: '#121212',
                }}
            >
                Choose Plan
            </HelloMuiLoadingButton>
        </div>
    );
};

const SelectPlanSection = () => {
    const filteredPlans = SubscriptionPlans.filter((plan) => ['Entry', 'Plus', 'Prime', 'Pro'].includes(plan.title));

    return (
        <div className="">
            <IntroductionSection />
            <PeriodSelector />
            <div className="flex items-center justify-center gap-2 mt-4">
                <div className="bg-my-blue bg-opacity-10 px-4 py-2 rounded-full">
                    <span className="text-[14px] font-medium text-my-blue">2 Day Free Trial</span>
                </div>
                <span className="text-[14px] text-gray-600">for all paid plans</span>
            </div>

            <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-[20px] pt-[36px] lg:max-w-[1244px] mx-auto">
                {filteredPlans.map((planobj: PlanTypes, index: number) => (
                    <div key={index}>
                        <PlanCard plan={planobj} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SelectPlanSection;
